import { ConfigService } from './services/config.service';
import {
  InjectionToken,
  NgModule,
  ModuleWithProviders,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {
  IPublicClientApplication,
  PublicClientApplication,
  LogLevel,
  InteractionType,
} from '@azure/msal-browser';
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
} from '@azure/msal-angular';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

const AUTH_CONFIG_URL_TOKEN = new InjectionToken<string>('AUTH_CONFIG_URL');

export function initializerFactory(env: ConfigService): any {
  const promise = env.init().then((value) => {
    console.log('finished getting configurations dynamically.');
  });
  return () => promise;
}

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

// Shared MSAL instance - created and initialized once
let msalInstance: IPublicClientApplication | null = null;
let msalInitPromise: Promise<IPublicClientApplication> | null = null;

export async function initializeMsalInstance(
  config: ConfigService
): Promise<IPublicClientApplication> {
  if (msalInstance) {
    return msalInstance;
  }

  if (msalInitPromise) {
    return msalInitPromise;
  }

  msalInitPromise = (async () => {
    const pca = new PublicClientApplication({
      auth: {
        clientId: config.getConfig('AzureClientID'),
        authority: 'https://login.microsoftonline.com/2ede383a-7e1f-4357-a846-85886b2c0c4d',
        redirectUri: config.getConfig('RedirectUri'),
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
      },
      system: {
        loggerOptions: {
          loggerCallback,
          logLevel: LogLevel.Warning,
          piiLoggingEnabled: false,
        },
      },
    });

    // MSAL Browser v4 requires explicit initialization
    await pca.initialize();
    msalInstance = pca;
    console.log('[MSAL] PublicClientApplication initialized successfully');
    return pca;
  })();

  return msalInitPromise;
}

export function MSALInstanceFactory(): IPublicClientApplication {
  if (!msalInstance) {
    throw new Error(
      'MSAL instance not initialized. Ensure initializeMsalInstance runs before accessing MSAL_INSTANCE.'
    );
  }
  return msalInstance;
}

export function MSALInterceptorConfigFactory(config: ConfigService): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>([
    ['https://graph.microsoft.com/v1.0/me', ['user.read']],
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(config: ConfigService): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
    loginFailedRoute: '/login-failed',
  };
}

@NgModule({
  providers: [],
  imports: [MsalModule],
})
export class MsalConfigDynamicModule {
  static forRoot(): ModuleWithProviders<MsalConfigDynamicModule> {
    return {
      ngModule: MsalConfigDynamicModule,
      providers: [
        // Provide HttpClient early so it's available during app initialization
        provideHttpClient(withInterceptorsFromDi()),
        ConfigService,
        // Combined initializer: load config THEN initialize MSAL
        provideAppInitializer(async () => {
          const configService = inject(ConfigService);
          await configService.init();
          console.log('finished getting configurations dynamically.');
          await initializeMsalInstance(configService);
        }),
        {
          provide: MSAL_INSTANCE,
          useFactory: MSALInstanceFactory,
          deps: [],
        },
        {
          provide: MSAL_GUARD_CONFIG,
          useFactory: MSALGuardConfigFactory,
          deps: [ConfigService],
        },
        {
          provide: MSAL_INTERCEPTOR_CONFIG,
          useFactory: MSALInterceptorConfigFactory,
          deps: [ConfigService],
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        {
          provide: HTTP_INTERCEPTORS,
          useClass: MsalInterceptor,
          multi: true,
        },
      ],
    };
  }
}
